import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { FeatureGeoJson } from "../FeatureGeoJson";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { Feature, Map } from "ol";
import ImageWMSEx from "../net/ImageWMSEx";
import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import ImageLayer from "ol/layer/Image";
import { RegularShape, Fill, Stroke, Style } from "ol/style";
import { AttributeType } from "../define/AttributeType";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { LayerUtil } from "../util/LayerUtil";
import { AccidentHotspotAttributeConvert } from "@/attribute/convert/AccidentHotspotAttributeConvert";

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 14;

/**
 * コンテキストメニュー名
 */
const CONTEXT_MENU_NAME = "交通事故統計情報";

/**
 * レイヤ名
 */
const LAYER_NAME = ["t_accident_hotspot"];

/**
 * 属性タイプ
 */
const ATTRIBUTE_TYPE = AttributeType.Normal;

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * 選択状態のスタイルを返却
 */
const FUNCTION_getSelcetedStyle = (): Style => {
  return new Style({
    image: new RegularShape({
      points: 4,
      radius: 7.5,
      radius2: 0,
      angle: Math.PI / 4,
      fill: new Fill({ color: "#ff69B4" }),
      stroke: new Stroke({
        color: "#ff69B4",
        width: 3,
      }),
    }),
  });
};

/**
 * 事故統計情報レイヤ出力クラス
 */
export class AccidentHotspotLayerFactory implements ILayerFactory {
  /**
   * 表示WMSレイヤ
   */
  private _wmsLayer: ImageLayer<ImageWMSEx>;

  /**
   * 表示WMSソース
   */
  private _wmsImageSource: ImageWMSEx;
  /**
   * 絞り込み条件
   */
  private _year: string[] = [];
  private _accidentDetail: string[] = [];
  private _accidenType: string[] = [];
  private _partyTypeA: string[] = [];
  private _partyTypeB: string[] = [];
  private _partyAgeA: string[] = [];
  private _partyAgeB: string[] = [];

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return true;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return true;
  }

  /**
   * レイヤ定義を取得
   * @param map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): ImageLayer<ImageWMSEx>[] {
    if (this._wmsLayer) {
      return [this._wmsLayer];
    }
    //WMSレイヤの取得
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: "t_accident_hotspot",
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._wmsLayer = new ImageLayer({
      source: this._wmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      minZoom: LAYER_MIN_ZOOMLEVEL,
    });
    return [this._wmsLayer];
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param layerNames レイヤ名リスト
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   * @param currentTime 対象時間
   */
  async getFeatureInfo(
    map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }

    const cqlParameters = LAYER_NAME.map((layerName) => {
      const cqlParameterString = `"発生日時_年" IN(${this._year
        .map((ele) => "'" + ele + "'")
        .join(",")})
         AND "事故内容" IN(${this._accidentDetail
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "事故類型" IN(${this._accidenType
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "当事者種別（当事者A）" IN(${this._partyTypeA
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "当事者種別（当事者B）" IN(${this._partyTypeB
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "年齢（当事者A）" IN(${this._partyAgeA
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "年齢（当事者B）" IN(${this._partyAgeB
           .map((ele) => "'" + ele + "'")
           .join(",")})`;
      return new CqlParameter(layerName, cqlParameterString);
    });

    const WMSGetter = new WMSGetFeatureManager();
    const buffer = 5;
    const selectedFeatureGeoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width,
      buffer
    ).catch((error) => {
      throw error;
    });

    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = await Promise.all(
      selectedFeatures.map(async (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];

        const selectedId = `${layerKey}-${feature.get("ジオメトリID")}`;

        const wfsCqlParameterString = `"ジオメトリID"='${feature.get(
          "ジオメトリID"
        )}'`;
        const wfsCqlParameters = [
          new CqlParameter(layerKey, wfsCqlParameterString),
        ];
        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          wfsCqlParameters
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON().readFeatures(wfsGeoJson)[0];
        if (!wfsFeature) return;

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });
        selectFeature.setId(selectedId);
        selectFeature.setStyle(FUNCTION_getSelcetedStyle());
        // featureGeoJSONを生成
        const geoJSON = new GeoJSON().writeFeature(wfsFeature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, selectedId);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          CONTEXT_MENU_NAME,
          featureGeoJSON,
          ATTRIBUTE_TYPE,
          new AccidentHotspotAttributeConvert(wfsFeature),
          false,
          layerKey
        );
        return selectedFeatureInfo;
      })
    );
    return selectFeatureInfoList;
  }

  /**
   * 指定範囲の地物情報を取得
   * @param map 地図
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // cqlパラメータクラスを作成
    const cqlParameters = LAYER_NAME.map((layerName) => {
      const cqlParameterString = `"発生日時_年" IN(${this._year
        .map((ele) => "'" + ele + "'")
        .join(",")})
         AND "事故内容" IN(${this._accidentDetail
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "事故類型" IN(${this._accidenType
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "当事者種別（当事者A）" IN(${this._partyTypeA
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "当事者種別（当事者B）" IN(${this._partyTypeB
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "年齢（当事者A）" IN(${this._partyAgeA
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "年齢（当事者B）" IN(${this._partyAgeB
           .map((ele) => "'" + ele + "'")
           .join(",")})`;
      return new CqlParameter(layerName, cqlParameterString);
    });
    const WFSGetter = new WFSGetFeatureManager();
    const selectedFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      cqlParameters,
      bBox
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = selectedFeatures.map(
      (feature) => {
        const id = feature.getId().toString();
        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });

        const geoJSON = new GeoJSON().writeFeature(feature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          CONTEXT_MENU_NAME,
          featureGeoJSON,
          ATTRIBUTE_TYPE,
          new AccidentHotspotAttributeConvert(feature),
          false
        );
        return selectedFeatureInfo;
      }
    );
    return selectFeatureInfoList;
  }

  /**
   * 地物属性の有無を取得
   */
  async getRightClickInfo(): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param layerNameList 対象TraVosLayerリスト
   */
  setVisible(
    isVisible: boolean,
    year: string[],
    accidentDetail: string[],
    accidenType: string[],
    partyTypeA: string[],
    partyTypeB: string[],
    partyAgeA: string[],
    partyAgeB: string[]
  ) {
    if (isVisible) {
      // isVisible: trueの場合、引数の条件を追加
      this._year = [...year];
      this._accidentDetail = [...accidentDetail];
      this._accidenType = [...accidenType];
      this._partyTypeA = [...partyTypeA];
      this._partyTypeB = [...partyTypeB];
      this._partyAgeA = [...partyAgeA];
      this._partyAgeB = [...partyAgeB];
      const cqlParameters: string[] = [];

      const cqlParameter = `"発生日時_年" IN(${year
        .map((ele) => "'" + ele + "'")
        .join(",")})
         AND "事故内容" IN(${accidentDetail
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "事故類型" IN(${accidenType
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "当事者種別（当事者A）" IN(${partyTypeA
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "当事者種別（当事者B）" IN(${partyTypeB
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "年齢（当事者A）" IN(${partyAgeA
           .map((ele) => "'" + ele + "'")
           .join(",")})
         AND "年齢（当事者B）" IN(${partyAgeB
           .map((ele) => "'" + ele + "'")
           .join(",")})`;
      cqlParameters.push(cqlParameter);
      this._wmsImageSource.updateParams({
        LAYERS: "t_accident_hotspot",
        EXCEPTIONS: "application/json",
        CQL_FILTER: cqlParameters.join(";"),
      });
    }
    this._wmsLayer.setVisible(isVisible);
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    return this._wmsLayer?.getVisible() ? 1 : 0;
  }

  /**
   * 表示レイヤが存在するか判定
   */
  isVisibleLayer(_map: Map) {
    return LayerUtil.isVisibleLayer(_map, this._wmsLayer);
  }
}
